import { Stack, TextField, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Configuration } from "shared/configuration"
import { DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS } from "shared/depositCodePrintData"

type LabelOffsetFieldsProps = {
  offsetX?: Configuration
  offsetY?: Configuration
  onChange: (config: Configuration) => void
}

/**
 * LOCKER-522 — moves the whole label on the roll, in printer dots. Rendered
 * only for the rows that exist (seed10 creates them on existing tenants).
 */
export const LabelOffsetFields: FC<LabelOffsetFieldsProps> = ({
  offsetX,
  offsetY,
  onChange,
}) => {
  const { t } = useTranslation()
  if (!offsetX && !offsetY) return null

  const field = (config: Configuration | undefined, labelKey: string) =>
    config && (
      <TextField
        size="small"
        type="number"
        label={t(labelKey)}
        sx={{ width: 220 }}
        value={config.nVal ?? ""}
        slotProps={{
          htmlInput: {
            min: -DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS,
            max: DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS,
            step: 1,
          },
        }}
        onChange={(e) => {
          // An empty field is a keystroke in progress, not 0 (LOCKER-324).
          const raw = e.target.value
          const n = Math.max(
            -DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS,
            Math.min(DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS, Number(raw)),
          )
          onChange({ ...config, nVal: raw === "" ? null : n } as Configuration)
        }}
      />
    )

  return (
    <Stack spacing={1}>
      <Typography variant="body1" fontWeight="bold">
        {t("labelOffsetTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t("labelOffsetDescription")}
      </Typography>
      <Stack direction="row" spacing={2}>
        {field(offsetX, "labelOffsetX")}
        {field(offsetY, "labelOffsetY")}
      </Stack>
    </Stack>
  )
}
